import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from 'react-redux';
import store from './redux/store';
import { ConfigProvider, theme as antdTheme } from 'antd';
import 'antd/dist/reset.css';
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { HashRouter } from "react-router-dom";
import { ToastProvider } from './components/ToastContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Keeps Ant Design's token-based theming (used by TestDesignChat, Tables,
// Modals, etc.) in sync with the app's CSS-variable dark/light theme — the
// tokens below mirror the corresponding `--bg-*`/`--text-*`/`--border-*`
// values in index.css so AntD components don't stay stuck in dark colors
// when the user switches to light mode.
const AntdThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDark } = useTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: isDark
          ? {
              colorPrimary: '#3B82F6',
              colorLink: '#3B82F6',
              colorLinkHover: '#2563EB',
              borderRadius: 8,
              colorBgContainer: '#1C2333',
              colorBgElevated: '#1C2333',
              colorBgLayout: '#111827',
              colorText: '#F0F4F8',
              colorTextSecondary: '#94A3B8',
              colorBorder: '#2D3A4E',
              colorBorderSecondary: '#2D3A4E',
            }
          : {
              colorPrimary: '#2563EB',
              colorLink: '#2563EB',
              colorLinkHover: '#1D4ED8',
              borderRadius: 8,
              colorBgContainer: '#FFFFFF',
              colorBgElevated: '#FFFFFF',
              colorBgLayout: '#F3F4F6',
              colorText: '#111827',
              colorTextSecondary: '#6B7280',
              colorBorder: '#D1D5DB',
              colorBorderSecondary: '#E5E7EB',
            },
      }}
    >
      {children}
    </ConfigProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <Provider store={store}>
        <ToastProvider>
          <HashRouter>
            <AntdThemeProvider>
              <App />
            </AntdThemeProvider>
          </HashRouter>
        </ToastProvider>
      </Provider>
    </ThemeProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
